/**
 * numeroDePage — ce que `set_header_footer` fait d'un numéro de page, hors de
 * l'éditeur : du texte d'un paragraphe aux champs à poser, et de la relecture
 * du fichier au verdict du reçu. Module pur ; l'exécuteur
 * (`headerFooterActionExecutors.ts`) parle seul à l'éditeur.
 *
 * ## Ce qui est mesuré (2026-10-06 et 2026-10-07)
 *
 * Sondes `tests/ws-mocked/mesure-socle-entetes-et-champs.spec.ts` et
 * `mesure-socle-numero-et-page.spec.ts`, gabarit Hector, suivi coupé, juge = le
 * fichier exporté ; relevé : §15 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`.
 *
 * 1. `fields.insert({at:{kind:'text', segments:[{blockId, range:{start,end}}], story}, instruction, mode:'raw'})`
 *    pose `PAGE` ou `NUMPAGES` dans un paragraphe d'en-tête ou de pied. Sans la
 *    story : `TARGET_NOT_FOUND`. Une entrée d'historique par champ, aucune
 *    révision suivie. Le fichier porte `w:fldSimple`.
 * 2. ⚠️ **Une position comptée après un champ déjà posé tombe à côté.** Le
 *    texte relu d'un paragraphe compte le résultat du champ (« Folio 1 sur »),
 *    la position d'insertion non : `NUMPAGES` posé après `PAGE`, à la position
 *    comptée sur le texte écrit, atterrit un caractère trop tôt (« sur[Y]  fin »
 *    au lieu de « sur [Y] fin ») ; comptée sur le texte relu, en début de
 *    paragraphe. Posés du PLUS LOIN au PLUS PRÈS, à des positions comptées sur
 *    le texte écrit, les deux champs tombent juste : aucune position ne dépend
 *    alors d'un champ déjà posé. C'est `ordreDePose`.
 * 3. `fields.list({})` publie chaque champ avec `address:{kind:'field', blockId,
 *    occurrenceIndex, storyId}` ; `storyId` vaut `footer:/word/footer1.xml` —
 *    le genre, puis le chemin de la partie dans l'archive. `occurrenceIndex`
 *    se décale quand un champ précédent du même paragraphe est retiré : la
 *    liste se relit avant chaque retrait.
 * 4. `fields.remove({target: address, mode:'raw'})` retire un champ, texte
 *    autour gardé ; une entrée d'historique.
 * 5. ⚠️ `blocks.delete` du SEUL paragraphe d'une partie laisse `<w:ftr></w:ftr>`,
 *    une partie sans paragraphe. Un paragraphe vide se crée par
 *    `create.paragraph({in: story, at:{kind:'documentEnd'}})`, sans `text`.
 * 6. ⚠️ `blocks.delete` d'un paragraphe qui porte encore les champs complexes
 *    d'un gabarit a jeté une fois `paragraph-tracked-wrapper-unsupported`
 *    (après une série d'annulations ; non reproduit autrement). Champs retirés
 *    d'abord, la suppression du même paragraphe a toujours abouti.
 */

import { CANVAS_HEADER_FOOTER_BLOCKS_MAX, CANVAS_PAGE_NUMBER_TOKENS, type CanvasHeaderFooterPageNumbers, type CanvasPageNumberField } from '@hector/shared-types';

/** L'instruction de champ que l'éditeur reçoit pour chaque numéro — et le nom sous lequel un reçu le dit au modèle. */
export const INSTRUCTION_DU_NUMERO: Readonly<Record<CanvasPageNumberField, 'PAGE' | 'NUMPAGES'>> = { page: 'PAGE', pages: 'NUMPAGES' };

/** Un champ à poser : sa place dans le texte écrit (nombre de caractères avant lui), et lequel. */
export interface ChampAPoser { position: number; numero: CanvasPageNumberField }

/** Le texte d'un paragraphe sans ses jetons, et les champs à y poser, dans l'ordre du texte. */
export interface TexteANumeros { texte: string; champs: ChampAPoser[] }

const JETON = new RegExp(`${CANVAS_PAGE_NUMBER_TOKENS.pages.replace(/[{}]/gu, '\\$&')}|${CANVAS_PAGE_NUMBER_TOKENS.page.replace(/[{}]/gu, '\\$&')}`, 'gu');

/**
 * Sépare le texte à écrire des numéros de page qu'il demande.
 *
 * Les jetons sont lus tels que le contrat les écrit, casse comprise : un texte
 * qui porte d'autres accolades est écrit tel quel.
 */
export function lireLesJetons(text: string): TexteANumeros {
  const champs: ChampAPoser[] = [];
  let texte = '';
  let lu = 0;
  for (const trouve of text.matchAll(JETON)) {
    texte += text.slice(lu, trouve.index);
    champs.push({ position: texte.length, numero: trouve[0] === CANVAS_PAGE_NUMBER_TOKENS.page ? 'page' : 'pages' });
    lu = trouve.index + trouve[0].length;
  }
  return { texte: texte + text.slice(lu), champs };
}

/** L'ordre dans lequel poser les champs d'un paragraphe : du plus loin au plus près (fait mesuré n° 2). */
export function ordreDePose(champs: readonly ChampAPoser[]): ChampAPoser[] {
  return [...champs].reverse();
}

/** Un paragraphe de la zone tel que le fichier exporté le rend (`lecteurDeForme.ts`). */
export interface ParagrapheRelu { nodeId: string | null; numerosDePage: readonly CanvasPageNumberField[] }

type Verdict = Pick<CanvasHeaderFooterPageNumbers, 'outcome' | 'fields_after'>;

/** Les champs de la zone, dans l'ordre du fichier, coupés à la borne du reçu (`page_numbers.fields_after`). */
const champsDe = (relus: readonly ParagrapheRelu[]): CanvasPageNumberField[] => relus.flatMap((relu) => [...relu.numerosDePage]).slice(0, CANVAS_HEADER_FOOTER_BLOCKS_MAX);

/**
 * Juge une pose : chaque paragraphe ÉCRIT par l'appel porte les champs demandés, dans l'ordre.
 *
 * @param ecrits Les paragraphes que l'appel a écrits, et les champs que chacun devait recevoir.
 * @param relus Les paragraphes de la zone relus au fichier ; `null` : fichier ou zone non relus.
 */
export function jugerLaPose(
  ecrits: ReadonlyArray<{ nodeId: string; attendus: readonly CanvasPageNumberField[] }>,
  relus: readonly ParagrapheRelu[] | null,
): Verdict {
  if (relus === null) return { outcome: 'unreadable', fields_after: null };
  const fields_after = champsDe(relus);
  let outcome: Verdict['outcome'] = 'conforming';
  for (const ecrit of ecrits) {
    const relu = relus.find((candidat) => candidat.nodeId === ecrit.nodeId);
    // Un paragraphe écrit que le fichier ne rend pas sous son identifiant : rien n'est établi sur lui.
    if (relu === undefined) return { outcome: 'unreadable', fields_after };
    if (relu.numerosDePage.length !== ecrit.attendus.length || relu.numerosDePage.some((numero, rang) => numero !== ecrit.attendus[rang])) outcome = 'different';
  }
  return { outcome, fields_after };
}

/**
 * Juge un retrait : la zone ne porte plus aucun numéro de page, et garde au
 * moins un paragraphe — une partie sans paragraphe n'est pas valide (fait
 * mesuré n° 5).
 */
export function jugerLeRetrait(relus: readonly ParagrapheRelu[] | null): Verdict {
  if (relus === null) return { outcome: 'unreadable', fields_after: null };
  const fields_after = champsDe(relus);
  return { outcome: fields_after.length === 0 && relus.length > 0 ? 'conforming' : 'different', fields_after };
}
